import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Select } from '@factorialco/f0-react'
import { F0ActionBar, NumberInput, SectionHeader } from '@factorialco/f0-react/dist/experimental'
import Add from '@factorialco/f0-react/icons/app/Add'
import {
  TrainingBudgetsDocument,
  TrainingClassDetailIndexPageDocument,
  TrainingsTrainingClassPaymentStatusEnum,
  UpdateCostsClassDetailDocument,
  UpdateCostsClassDetailMutationVariables
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { checkFeatures } from 'modules/core/hooks/useFeatures'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavConfirmModal } from 'modules/core/hooks/useNavConfirmModal'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { SalaryCostCalculator } from 'modules/trainings/components/SalaryCostCalculator'

import CostsTabNew from './CostsTabNew'

interface ClassCostsValues {
  cost: string
  subsidizedCost?: string
  indirectCost?: string
  salaryCost?: string
  paymentStatus?: TrainingsTrainingClassPaymentStatusEnum | null
  budgetId?: number | null
}

const CostsTabOld = () => {
  const { classId } = useParams()
  const { company } = useCurrentSession()

  // companyId is 0 because we are not using the companyId to get the feature flag
  const { data } = useQuery(TrainingClassDetailIndexPageDocument, {
    variables: { companyId: company.id, trainingClassId: Number(classId), withFF: true }
  })
  const currentClass = data?.trainings.trainingClass ?? undefined

  const { has_dev_training_budgets } = checkFeatures(
    ['dev_training_budgets'],
    data?.features?.featuresConnection.nodes
  )

  const { data: budgetsData } = useQuery(TrainingBudgetsDocument, {
    variables: {
      budgetableBy: ['training_class']
    },
    skip: !has_dev_training_budgets
  })

  const budgetOptions = (budgetsData?.finance.budgetsConnection.edges || []).map((edge) => ({
    value: edge.node.id,
    label: edge.node.name
  }))

  const [updateCosts] = useMutation(UpdateCostsClassDetailDocument, {
    extractorFn(d) {
      return d.trainingsMutations.updateTrainingsTrainingClass
    }
  })

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const { NavConfirmModal, setNavigationBlocked } = useNavConfirmModal()

  useEffect(() => {
    setNavigationBlocked(hasUnsavedChanges)
  }, [hasUnsavedChanges, setNavigationBlocked])

  const { values, setValues, handleSubmit, errors, validateForm, resetForm } =
    useForm<ClassCostsValues>({
      enableReinitialize: true,
      defaultValues: {
        cost: currentClass?.cost || '',
        subsidizedCost: currentClass?.subsidizedCost || '',
        indirectCost: currentClass?.indirectCost || '',
        salaryCost: currentClass?.salaryCost || '',
        paymentStatus: currentClass?.paymentStatus,
        budgetId: currentClass?.budgetMovement?.budget?.id ?? null
      },
      validate: (formData) => {
        const validationErrors: FormErrors<ClassCostsValues> = {}
        if (Number(formData.cost) < 0) {
          validationErrors.cost = i18n.t('trainings.classes.errors.greater_than_zero')
        }
        if (Number(formData.cost) > 999999) {
          validationErrors.cost = i18n.t('trainings.classes.errors.less_than')
        }
        if (Number(formData.subsidizedCost) < 0) {
          validationErrors.subsidizedCost = i18n.t('trainings.classes.errors.greater_than_zero')
        }
        if (Number(formData.subsidizedCost) > 999999) {
          validationErrors.subsidizedCost = i18n.t('trainings.classes.errors.less_than')
        }
        if (Number(formData.indirectCost) < 0) {
          validationErrors.indirectCost = i18n.t('trainings.classes.errors.greater_than_zero')
        }
        if (Number(formData.indirectCost) > 999999) {
          validationErrors.indirectCost = i18n.t('trainings.classes.errors.less_than')
        }
        if (Number(formData.salaryCost) < 0) {
          validationErrors.salaryCost = i18n.t('trainings.classes.errors.greater_than_zero')
        }
        if (Number(formData.salaryCost) > 999999) {
          validationErrors.salaryCost = i18n.t('trainings.classes.errors.less_than')
        }
        return validationErrors
      },
      onSubmit: async (classData) => {
        setIsSaving(true)
        try {
          const payload: UpdateCostsClassDetailMutationVariables = {
            id: Number(currentClass?.id),
            ...classData
          }
          const result = await updateCosts({
            variables: {
              id: payload.id,
              cost: payload.cost && payload.cost.length > 0 ? payload.cost : '0',
              salaryCost:
                payload.salaryCost && payload.salaryCost?.length > 0 ? payload.salaryCost : '0',
              indirectCost:
                payload.indirectCost && payload.indirectCost.length > 0
                  ? payload.indirectCost
                  : '0',
              subsidizedCost:
                payload.subsidizedCost && payload.subsidizedCost?.length > 0
                  ? payload.subsidizedCost
                  : '0',
              paymentStatus: payload.paymentStatus,
              budgetId: payload.budgetId
            },
            refetchQueries: [TrainingClassDetailIndexPageDocument]
          })
          if (result) {
            const handled = handleResult(result, () => {})
            if (handled.success) {
              setHasUnsavedChanges(false)
            }
            return handled
          }
          return Promise.reject()
        } finally {
          setIsSaving(false)
        }
      }
    })

  const handleFieldChange = async (
    field: keyof ClassCostsValues,
    newValue: string | TrainingsTrainingClassPaymentStatusEnum | number | null
  ) => {
    const updatedPaymentStatus: ClassCostsValues['paymentStatus'] =
      field === 'budgetId' && newValue != null && !values.paymentStatus
        ? 'pending'
        : values.paymentStatus

    const newValues = {
      ...values,
      paymentStatus: updatedPaymentStatus,
      [field]: newValue
    }

    setValues(newValues)
    setHasUnsavedChanges(true)
    await validateForm(newValues)
  }

  const handleDiscard = () => {
    resetForm()
    setHasUnsavedChanges(false)
  }

  const currency = currentClass?.currency || company.currency || ''
  return (
    <>
      <Box width="full">
        <SectionHeader
          title={i18n.t('trainings.trainings_revamp.classes.cost_header.title')}
          description={i18n.t('trainings.trainings_revamp.classes.cost_header.description')}
          separator="bottom"
        />
        <Box paddingTop="s20" flexDirection="column" gap="s16">
          <Box gap="s8">
            <Text size="300" color="textPrimary" weight="semibold">
              {i18n.t('trainings.trainings_detail.fundae.costs.direct_costs.label')}
            </Text>
            <Text size="200">{i18n.t('trainings.classes.cost_desc')}</Text>
            <Box maxWidth="s400">
              <NumberInput
                locale={i18n.locale}
                label={i18n.t('trainings.classes.cost_desc')}
                hideLabel
                units={currency}
                onChange={(value) => handleFieldChange('cost', value !== null ? String(value) : '')}
                error={errors.cost}
                value={values.cost ? Number(values.cost) : null}
                maxDecimals={2}
                size="md"
              />
            </Box>
          </Box>

          <Box gap="s8">
            <Text size="300" color="textPrimary" weight="semibold">
              {i18n.t('trainings.trainings_detail.fundae.costs.indirect_costs.label')}
            </Text>
            <Text size="200">{i18n.t('trainings.classes.indirect_cost_desc')}</Text>
            <Box maxWidth="s400">
              <NumberInput
                label={i18n.t('trainings.classes.indirect_cost_desc')}
                hideLabel
                locale={i18n.locale}
                units={currency}
                error={errors.indirectCost}
                onChange={(value) =>
                  handleFieldChange('indirectCost', value !== null ? String(value) : '')
                }
                value={values.indirectCost ? Number(values.indirectCost) : null}
                maxDecimals={2}
                size="md"
              />
            </Box>
          </Box>

          <Box gap="s8">
            <Text size="300" color="textPrimary" weight="semibold">
              {i18n.t('trainings.trainings_detail.fundae.costs.salary_costs.label')}
            </Text>
            <Text size="200">{i18n.t('trainings.classes.salary_cost_desc')}</Text>
            <Box gap="s16" flexDirection="row" alignItems="center">
              <Box maxWidth="s400">
                <NumberInput
                  label={i18n.t('trainings.classes.salary_cost_desc')}
                  hideLabel
                  locale={i18n.locale}
                  units={currency}
                  error={errors.salaryCost}
                  onChange={(value) =>
                    handleFieldChange('salaryCost', value !== null ? String(value) : '')
                  }
                  value={values.salaryCost ? Number(values.salaryCost) : null}
                  maxDecimals={2}
                  size="md"
                />
              </Box>
              <SalaryCostCalculator
                classId={Number(currentClass?.id)}
                onApply={(newValue) => {
                  handleFieldChange('salaryCost', newValue)
                  return true
                }}
                trackingSelectedFrom="groups_cost_tab"
                currency={currency}
              />
            </Box>
          </Box>

          <Box gap="s8">
            <Text size="300" color="textPrimary" weight="semibold">
              {i18n.t('trainings.trainings_revamp.training_class.class_wizard.total_cost_group')}
            </Text>
            <Text size="200">
              {i18n.t(
                'trainings.trainings_revamp.training_class.class_wizard.total_cost_group_desc'
              )}
            </Text>
            <Box gap="s16" flexDirection="row" alignItems="center" maxWidth="s400">
              <Box minWidth="s200">
                <NumberInput
                  label={i18n.t(
                    'trainings.trainings_revamp.training_class.class_wizard.total_cost_group'
                  )}
                  hideLabel
                  locale={i18n.locale}
                  units={currency}
                  disabled
                  value={currentClass?.grossCost ? Number(currentClass.grossCost) : 0}
                  maxDecimals={2}
                  size="md"
                />
              </Box>
              {has_dev_training_budgets && (
                <Box width="full">
                  <F0Select
                    label={i18n.t('trainings.classes.select_budget')}
                    hideLabel
                    placeholder={i18n.t('trainings.classes.select_budget')}
                    showSearchBox
                    searchBoxPlaceholder={i18n.t('common.search')}
                    options={budgetOptions.map((b) => ({
                      label: b.label,
                      value: String(b.value)
                    }))}
                    value={values.budgetId ? String(values.budgetId) : undefined}
                    onChange={(value: string | null) =>
                      handleFieldChange('budgetId', value ? Number(value) : null)
                    }
                    size="md"
                    actions={[
                      {
                        label: i18n.t('trainings.classes.create_budget'),
                        onClick: () => {},
                        variant: 'ghost',
                        icon: Add
                      }
                    ]}
                  />
                </Box>
              )}
            </Box>
          </Box>

          {has_dev_training_budgets && (
            <Box gap="s8">
              <Box maxWidth="s200">
                <F0Select<TrainingsTrainingClassPaymentStatusEnum>
                  label={i18n.t('trainings.classes.payment_status.label')}
                  options={[
                    {
                      value: 'pending',
                      label: i18n.t('trainings.classes.payment_status.pending')
                    },
                    {
                      value: 'paid',
                      label: i18n.t('trainings.classes.payment_status.paid')
                    }
                  ]}
                  value={values.paymentStatus}
                  onChange={(value: TrainingsTrainingClassPaymentStatusEnum) =>
                    handleFieldChange('paymentStatus', value)
                  }
                  size="md"
                />
              </Box>
            </Box>
          )}

          <Box gap="s8">
            <Text size="300" color="textPrimary" weight="semibold">
              {i18n.t(
                'trainings.trainings_revamp.training_class.class_wizard.basic_information_step.reviewed_subsidized_cost'
              )}
            </Text>
            <Text size="200">{i18n.t('trainings.classes.subsidized_cost_desc')}</Text>
            <Box maxWidth="s400">
              <NumberInput
                label={i18n.t('trainings.classes.subsidized_cost_desc')}
                hideLabel
                error={errors.subsidizedCost}
                locale={i18n.locale}
                units={currency}
                value={values.subsidizedCost ? Number(values.subsidizedCost) : null}
                onChange={(value) =>
                  handleFieldChange('subsidizedCost', value !== null ? String(value) : '')
                }
                maxDecimals={2}
                size="md"
              />
            </Box>
          </Box>
        </Box>
      </Box>

      <NavConfirmModal />

      {hasUnsavedChanges && (
        <F0ActionBar
          isOpen
          primaryActions={[
            {
              label: i18n.t('trainings.content.builder.save'),
              onClick: handleSubmit,
              disabled: isSaving
            }
          ]}
          secondaryActions={[
            { label: i18n.t('trainings.content.builder.discard'), onClick: handleDiscard }
          ]}
          label={i18n.t('trainings.content.builder.unsaved_changes')}
        />
      )}
    </>
  )
}

const CostsTab = () => {
  const { classId } = useParams()
  const { company } = useCurrentSession()

  const { data } = useQuery(TrainingClassDetailIndexPageDocument, {
    variables: { companyId: company.id, trainingClassId: Number(classId), withFF: true }
  })

  const { has_dev_training_budgets } = checkFeatures(
    ['dev_training_budgets'],
    data?.features?.featuresConnection.nodes
  )

  if (has_dev_training_budgets) {
    return <CostsTabNew />
  }

  return <CostsTabOld />
}

export default CostsTab
